import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Calendar, DollarSign, Gift, Plus, Edit2, Trash2, 
  ChevronDown, ChevronUp, Search, Download, RefreshCw, CheckCircle,
  XCircle, Clock, Copy, ExternalLink, BarChart3, TrendingUp, Eye, Sparkles,
  FileDown, Activity, Upload, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Generate a unique access code
const generateAccessCode = (orgName = '') => {
  const prefix = orgName
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 4)
    .toUpperCase() || 'CODE';
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}${year}${random}`;
};

// Simple bar chart component
const SimpleBarChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.count), 1);
  const barWidth = Math.max(4, Math.floor(100 / data.length) - 1);
  
  return (
    <div className="relative" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-[1px]">
        {data.map((item, i) => (
          <div
            key={i}
            className="bg-gradient-to-t from-[#df1283] to-[#FF7901] rounded-t hover:opacity-80 transition-opacity group relative"
            style={{
              width: `${barWidth}%`,
              height: `${Math.max((item.count / maxValue) * 100, 2)}%`,
              minHeight: item.count > 0 ? '4px' : '2px'
            }}
            title={`${item.date}: ${item.count} redemptions`}
          >
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a0527] px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
              {item.date}: {item.count}
            </div>
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 border-t border-[#4A0A66]/50" />
    </div>
  );
};

// Analytics Dashboard Component
const PartnershipAnalytics = ({ summary }) => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchTrends();
  }, [period]);

  const fetchTrends = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/partnerships/analytics/trends?days=${period}`, { 
        withCredentials: true 
      });
      setTrends(res.data);
    } catch (err) {
      console.error('Error fetching trends:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API_BASE}/api/admin/partnerships/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partnerships_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#4A0A66]/30 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#0FFFE2]" />
          Redemption Trends
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-[#1a0527] border border-[#4A0A66] text-white text-sm rounded px-2 py-1"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            onClick={handleExportCSV}
            disabled={exporting}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
            data-testid="export-csv-button"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-1" />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-[#df1283]" />
        </div>
      ) : trends ? (
        <div className="space-y-4">
          {/* Main Chart */}
          <div className="bg-[#1a0527] rounded-lg p-4">
            <div className="text-xs text-[#FDF2F8]/50 mb-2">Daily Redemptions</div>
            <SimpleBarChart data={trends.daily_totals} height={100} />
            <div className="flex justify-between text-xs text-[#FDF2F8]/40 mt-2">
              <span>{trends.daily_totals[0]?.date}</span>
              <span>{trends.daily_totals[trends.daily_totals.length - 1]?.date}</span>
            </div>
          </div>

          {/* Top Partnerships by Redemptions */}
          {trends.partnership_trends?.length > 0 && (
            <div className="bg-[#1a0527] rounded-lg p-4">
              <div className="text-xs text-[#FDF2F8]/50 mb-3">Seat Utilization by Organization</div>
              <div className="space-y-2">
                {trends.partnership_trends.slice(0, 5).map((p, i) => {
                  const utilization = p.total_seats > 0 ? (p.total_redeemed / p.total_seats) * 100 : 0;
                  return (
                    <div key={p.partnership_id} className="flex items-center gap-3">
                      <div className="w-6 text-center text-xs text-[#FDF2F8]/50">#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{p.organization_name}</div>
                        <div className="h-2 bg-[#4A0A66]/30 rounded-full overflow-hidden mt-1">
                          <div
                            className="h-full bg-gradient-to-r from-[#df1283] to-[#FF7901] rounded-full transition-all"
                            style={{ width: `${Math.min(utilization, 100)}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-white font-medium">{p.total_redeemed}/{p.total_seats}</div>
                        <div className="text-xs text-[#FDF2F8]/50">{utilization.toFixed(0)}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-[#FDF2F8]/50">
          No trend data available
        </div>
      )}
    </div>
  );
};

// Partnership Detail Analytics Modal Component
const PartnershipDetailModal = ({ partnership, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(90);
  const [viewMode, setViewMode] = useState(partnership.viewMode || 'analytics');

  useEffect(() => {
    fetchAnalytics();
  }, [partnership.partnership_id, period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/api/admin/partnerships/${partnership.partnership_id}/analytics?days=${period}`,
        { withCredentials: true }
      );
      setAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching partnership analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr.split('T')[0];
    }
  };

  // Members-only view (simplified)
  if (viewMode === 'members') {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#2D063E] rounded-xl border border-[#4A0A66] w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-[#4A0A66] flex items-center justify-between shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FF7901]" />
                {partnership.organization_name} Members
              </h3>
              <p className="text-sm text-[#FDF2F8]/60">
                {analytics?.total_user_count || 0} members • Code: <span className="font-mono text-[#0FFFE2]">{partnership.access_code}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setViewMode('analytics')} 
                className="text-xs text-[#0FFFE2] hover:underline"
              >
                View Full Analytics
              </button>
              <button onClick={onClose} className="text-white/50 hover:text-white ml-2">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Members List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#df1283]" />
              </div>
            ) : analytics?.users && analytics.users.length > 0 ? (
              <div className="divide-y divide-[#4A0A66]/30">
                {analytics.users.map((user, i) => (
                  <div key={user.user_id || i} className="flex items-center justify-between p-4 hover:bg-[#1a0527]/50">
                    <div className="flex-1">
                      <div className="text-white font-medium">{user.name}</div>
                      <div className="text-sm text-[#FDF2F8]/60">{user.email}</div>
                      <div className="text-xs text-[#FDF2F8]/40 mt-1">
                        Joined {formatDate(user.joined_at)} • {user.entry_count} entries
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post(`${API_BASE}/api/admin/partnerships/resend-welcome-email/${user.user_id}`, {}, { withCredentials: true });
                          toast.success(`Welcome email sent to ${user.email}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to send email');
                        }
                      }}
                      className="px-3 py-2 bg-[#FF7901]/20 hover:bg-[#FF7901]/30 text-[#FF7901] rounded-lg text-sm font-medium flex items-center gap-2 transition-colors border border-[#FF7901]/30"
                    >
                      <Mail className="w-4 h-4" />
                      Resend Email
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-[#FDF2F8]/50">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No members have redeemed this code yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full analytics view (original)
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#2D063E] rounded-xl border border-[#4A0A66] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#4A0A66] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0FFFE2]" />
              {partnership.organization_name} Analytics
            </h3>
            <p className="text-sm text-[#FDF2F8]/60">
              Access Code: <span className="font-mono text-[#0FFFE2]">{partnership.access_code}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="bg-[#1a0527] border border-[#4A0A66] text-white text-sm rounded px-2 py-1"
              data-testid="analytics-period-select"
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
            <button onClick={onClose} className="text-white/50 hover:text-white">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#df1283]" />
            </div>
          ) : analytics ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3">
                  <div className="text-2xl font-bold text-[#0FFFE2]">
                    {analytics.summary.total_redemptions}
                    <span className="text-sm text-[#FDF2F8]/60 font-normal ml-1">
                      / {analytics.partnership.total_seats}
                    </span>
                  </div>
                  <div className="text-xs text-[#FDF2F8]/60">Seats Used</div>
                  <div className="mt-1 h-1.5 bg-[#4A0A66]/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#df1283] to-[#0FFFE2] rounded-full"
                      style={{ width: `${Math.min(analytics.summary.utilization_rate, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3">
                  <div className="text-2xl font-bold text-[#FFD700]">
                    {analytics.summary.engagement_rate}%
                  </div>
                  <div className="text-xs text-[#FDF2F8]/60">Engagement Rate</div>
                  <div className="text-xs text-[#0FFFE2] mt-1">
                    {analytics.summary.users_with_entries} users journaling
                  </div>
                </div>
                <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3">
                  <div className="text-2xl font-bold text-[#FF7901]">
                    {analytics.summary.total_entries.toLocaleString()}
                  </div>
                  <div className="text-xs text-[#FDF2F8]/60">Total Entries</div>
                  <div className="text-xs text-[#FDF2F8]/40 mt-1">
                    ~{analytics.summary.avg_entries_per_user} per user
                  </div>
                </div>
                <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">
                    {analytics.summary.redemptions_per_week}
                  </div>
                  <div className="text-xs text-[#FDF2F8]/60">Redemptions/Week</div>
                  <div className="text-xs text-[#FDF2F8]/40 mt-1">
                    Peak: {analytics.summary.peak_day.count} on {analytics.summary.peak_day.date}
                  </div>
                </div>
              </div>

              {/* Redemption Timeline Chart */}
              <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#0FFFE2]" />
                  Seat Redemptions Over Time
                </h4>
                <div className="h-[140px] relative">
                  {analytics.timeline && analytics.timeline.length > 0 ? (
                    <>
                      {/* Cumulative line representation */}
                      <div className="flex items-end justify-between h-full gap-[1px]">
                        {analytics.timeline.filter((_, i) => i % Math.ceil(analytics.timeline.length / 60) === 0).map((item, i, arr) => {
                          const maxTotal = Math.max(...arr.map(d => d.total), 1);
                          return (
                            <div
                              key={i}
                              className="flex-1 relative group"
                              style={{ minWidth: '2px' }}
                            >
                              {/* Cumulative bar */}
                              <div
                                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#4A0A66] to-[#df1283]/60 rounded-t"
                                style={{ height: `${(item.total / maxTotal) * 100}%`, minHeight: item.total > 0 ? '2px' : '0' }}
                              />
                              {/* New redemptions indicator */}
                              {item.new > 0 && (
                                <div
                                  className="absolute left-0 right-0 bg-[#0FFFE2] rounded-t"
                                  style={{ 
                                    bottom: `${(item.total / maxTotal) * 100}%`,
                                    height: '3px'
                                  }}
                                />
                              )}
                              {/* Tooltip */}
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#1a0527] px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10 border border-[#4A0A66]">
                                <div>{item.date}</div>
                                <div className="text-[#0FFFE2]">Total: {item.total}</div>
                                {item.new > 0 && <div className="text-[#FFD700]">+{item.new} new</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-[#FDF2F8]/40 mt-2">
                        <span>{analytics.timeline[0]?.date}</span>
                        <span>{analytics.timeline[analytics.timeline.length - 1]?.date}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-[#FDF2F8]/50">
                      No redemption data yet
                    </div>
                  )}
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg overflow-hidden">
                <div className="p-3 border-b border-[#4A0A66] flex items-center justify-between">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#0FFFE2]" />
                    Members ({analytics.total_user_count})
                  </h4>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {analytics.users && analytics.users.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-[#2D063E] sticky top-0">
                        <tr className="text-left text-[#FDF2F8]/60">
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Joined</th>
                          <th className="px-3 py-2 text-right">Entries</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#4A0A66]/30">
                        {analytics.users.map((user, i) => (
                          <tr key={user.user_id || i} className="hover:bg-[#2D063E]/50">
                            <td className="px-3 py-2 text-white">{user.name}</td>
                            <td className="px-3 py-2 text-[#FDF2F8]/70">{user.email}</td>
                            <td className="px-3 py-2 text-[#FDF2F8]/60">{formatDate(user.joined_at)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className={`font-medium ${user.entry_count > 0 ? 'text-[#0FFFE2]' : 'text-[#FDF2F8]/40'}`}>
                                {user.entry_count}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API_BASE}/api/admin/partnerships/resend-welcome-email/${user.user_id}`, {}, { withCredentials: true });
                                    toast.success(`Welcome email sent to ${user.email}`);
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to send email');
                                  }
                                }}
                                className="text-xs px-2 py-1 bg-[#FF7901]/20 hover:bg-[#FF7901]/30 text-[#FF7901] rounded flex items-center gap-1 ml-auto transition-colors"
                                title="Resend welcome email"
                              >
                                <Mail className="w-3 h-3" />
                                Resend
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-6 text-center text-[#FDF2F8]/50">
                      No members have redeemed this code yet
                    </div>
                  )}
                </div>
                {analytics.total_user_count > 100 && (
                  <div className="p-2 border-t border-[#4A0A66] text-center text-xs text-[#FDF2F8]/50">
                    Showing first 100 of {analytics.total_user_count} members
                  </div>
                )}
              </div>

              {/* Partnership Details */}
              <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#0FFFE2]" />
                  Partnership Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-[#FDF2F8]/60">Status</div>
                    <div className={`font-medium ${analytics.partnership.status === 'active' ? 'text-[#0FFFE2]' : 'text-[#FFD700]'}`}>
                      {analytics.partnership.status?.charAt(0).toUpperCase() + analytics.partnership.status?.slice(1)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#FDF2F8]/60">Tier</div>
                    <div className="text-white font-medium">
                      {analytics.partnership.tier?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#FDF2F8]/60">Start Date</div>
                    <div className="text-white">{formatDate(analytics.partnership.start_date)}</div>
                  </div>
                  <div>
                    <div className="text-[#FDF2F8]/60">End Date</div>
                    <div className="text-white">{formatDate(analytics.partnership.end_date)}</div>
                  </div>
                  <div>
                    <div className="text-[#FDF2F8]/60">Payment Status</div>
                    <div className="text-white">{analytics.partnership.payment_status}</div>
                  </div>
                  <div>
                    <div className="text-[#FDF2F8]/60">Value</div>
                    <div className="text-[#FF7901] font-medium">
                      ${analytics.partnership.payment_amount?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-[#FDF2F8]/50">
              Failed to load analytics
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Partnership Types Manager Component
const PartnershipTypesManager = ({ types, onTypesUpdate }) => {
  const [newType, setNewType] = useState({ label: '', color: '#4A0A66' });
  const [adding, setAdding] = useState(false);

  const handleAddType = async () => {
    if (!newType.label) return;
    
    setAdding(true);
    try {
      await axios.post(`${API_BASE}/api/admin/partnership-types`, {
        id: newType.label.toLowerCase().replace(/\s+/g, '-'),
        label: newType.label,
        color: newType.color
      }, { withCredentials: true });
      
      toast.success('Partnership type added');
      setNewType({ label: '', color: '#4A0A66' });
      onTypesUpdate();
    } catch (err) {
      toast.error('Failed to add type');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Delete this partnership type?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/partnership-types/${typeId}`, { withCredentials: true });
      toast.success('Type deleted');
      onTypesUpdate();
    } catch (err) {
      toast.error('Failed to delete type');
    }
  };

  return (
    <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4 mb-6">
      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
        <Gift className="w-4 h-4 text-[#0FFFE2]" />
        Partnership Types
      </h4>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {types.map(type => (
          <div 
            key={type.id}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
            style={{ background: `${type.color}20`, border: `1px solid ${type.color}` }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: type.color }} />
            <span className="text-white">{type.label}</span>
            <button
              onClick={() => handleDeleteType(type.id)}
              className="text-white/50 hover:text-red-400 ml-1"
            >
              <XCircle className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      
      <div className="flex gap-2">
        <Input
          value={newType.label}
          onChange={(e) => setNewType({ ...newType, label: e.target.value })}
          placeholder="New type name..."
          className="bg-[#2D063E] border-[#4A0A66] text-white flex-1"
        />
        <input
          type="color"
          value={newType.color}
          onChange={(e) => setNewType({ ...newType, color: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer"
        />
        <Button
          onClick={handleAddType}
          disabled={adding || !newType.label}
          className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80"
        >
          {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

// Create/Edit Partnership Modal - Clean Tab-based Design
const PartnershipModal = ({ partnership, types, onClose, onSave }) => {
  const isEdit = !!partnership;
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    organization_name: partnership?.organization_name || '',
    contacts: partnership?.contacts || [{ first_name: '', last_name: '', email: '', phone: '', role: 'Primary Contact' }],
    partnership_type: partnership?.partnership_type || 'non-profit',
    access_code: partnership?.access_code || '',
    total_seats: partnership?.total_seats || 50,
    tier: partnership?.tier || 'premium',
    start_date: partnership?.start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    end_date: partnership?.end_date?.split('T')[0] || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    expiration_behavior: partnership?.expiration_behavior || 'downgrade_to_free',
    payment_status: partnership?.payment_status || 'free',
    payment_amount: partnership?.payment_amount || 0,
    notes: partnership?.notes || '',
    client_logo_url: partnership?.client_logo_url || '',
    brand_color: partnership?.brand_color || '',
    show_seats_remaining: partnership?.show_seats_remaining !== false,
    skip_onboarding: partnership?.skip_onboarding === true
  });
  const [saving, setSaving] = useState(false);

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { first_name: '', last_name: '', email: '', phone: '', role: '' }]
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, contacts: newContacts });
  };

  const removeContact = (index) => {
    if (formData.contacts.length === 1) return;
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    if (!formData.organization_name || !formData.access_code) {
      toast.error('Organization name and access code are required');
      return;
    }
    
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API_BASE}/api/admin/partnerships/${partnership.partnership_id}`, formData, { withCredentials: true });
        toast.success('Partnership updated');
      } else {
        await axios.post(`${API_BASE}/api/admin/partnerships`, {
          ...formData,
          start_date: new Date(formData.start_date).toISOString(),
          end_date: new Date(formData.end_date).toISOString()
        }, { withCredentials: true });
        toast.success('Partnership created');
      }
      onSave();
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) and string errors
      const detail = err.response?.data?.detail;
      let errorMsg = 'Failed to save partnership';
      if (detail) {
        if (Array.isArray(detail)) {
          // Pydantic returns [{loc: [...], msg: "...", type: "..."}]
          const firstError = detail[0];
          if (firstError?.msg) {
            // Map field names to friendly labels
            const fieldName = firstError.loc?.[firstError.loc.length - 1] || '';
            const fieldLabels = {
              'brand_color': 'Brand Color',
              'client_logo_url': 'Partner Logo URL'
            };
            const label = fieldLabels[fieldName] || fieldName;
            errorMsg = label ? `${label}: ${firstError.msg}` : firstError.msg;
          }
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#2D063E] rounded-2xl border border-[#4A0A66] w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-[#4A0A66] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-white">
              {isEdit ? 'Edit Partnership' : 'New Partnership'}
            </h3>
            <p className="text-sm text-[#FDF2F8]/50 mt-1">
              {isEdit ? `Editing ${partnership.organization_name}` : 'Set up a new organization partnership'}
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#4A0A66] shrink-0">
          {[
            { id: 'details', label: 'Details', icon: Building2 },
            { id: 'contacts', label: 'Contacts', icon: Users },
            { id: 'settings', label: 'Settings', icon: Clock },
            { id: 'branding', label: 'Branding', icon: Sparkles }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'text-[#0FFFE2] border-b-2 border-[#0FFFE2] -mb-[1px]' 
                  : 'text-[#FDF2F8]/60 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              <div>
                <label className="text-sm text-white font-medium block mb-2">Organization Name *</label>
                <Input
                  data-testid="partnership-organization-input"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  placeholder="e.g., Hope Foundation"
                  className="bg-[#1a0527] border-[#4A0A66] text-white h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Partnership Type</label>
                  <select
                    data-testid="partnership-type-select"
                    value={formData.partnership_type}
                    onChange={(e) => setFormData({ ...formData, partnership_type: e.target.value })}
                    className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md h-11 px-3"
                  >
                    {types.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Membership Tier</label>
                  <select
                    value={formData.tier}
                    onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                    className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md h-11 px-3"
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                    <option value="founding_member">Founding Member</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-white font-medium block mb-2">Access Code *</label>
                <div className="flex gap-3">
                  <Input
                    data-testid="partnership-access-code-input"
                    value={formData.access_code}
                    onChange={(e) => setFormData({ ...formData, access_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., HOPE2025ABC"
                    className="bg-[#1a0527] border-[#4A0A66] text-white uppercase flex-1 h-11 font-mono"
                    disabled={isEdit}
                  />
                  {!isEdit && (
                    <Button
                      type="button"
                      onClick={() => setFormData({ ...formData, access_code: generateAccessCode(formData.organization_name) })}
                      className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80 h-11 px-4"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#FDF2F8]/40 mt-2">Users enter this code at /redeem to join</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Total Seats</label>
                  <Input
                    type="number"
                    value={formData.total_seats}
                    onChange={(e) => setFormData({ ...formData, total_seats: parseInt(e.target.value) || 0 })}
                    className="bg-[#1a0527] border-[#4A0A66] text-white h-11"
                  />
                </div>
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Payment Status</label>
                  <select
                    value={formData.payment_status}
                    onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                    className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md h-11 px-3"
                  >
                    <option value="free">Free / Donated</option>
                    <option value="paid">Paid</option>
                    <option value="sponsored">Sponsored</option>
                  </select>
                </div>
              </div>

              {formData.payment_status === 'paid' && (
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Payment Amount ($)</label>
                  <Input
                    data-testid="partnership-value-input"
                    type="number"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({ ...formData, payment_amount: parseFloat(e.target.value) || 0 })}
                    className="bg-[#1a0527] border-[#4A0A66] text-white h-11"
                    placeholder="0.00"
                  />
                </div>
              )}

              <div>
                <label className="text-sm text-white font-medium block mb-2">Notes (Internal)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Internal notes about this partnership..."
                  className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md p-3 min-h-[80px] resize-none"
                />
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">Organization Contacts</h4>
                  <p className="text-sm text-[#FDF2F8]/50">Add contacts who manage this partnership</p>
                </div>
                <Button
                  onClick={addContact}
                  size="sm"
                  className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </div>

              {formData.contacts.map((contact, idx) => (
                <div key={idx} className="bg-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#FDF2F8]/60">Contact {idx + 1}</span>
                    {formData.contacts.length > 1 && (
                      <button
                        onClick={() => removeContact(idx)}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#FDF2F8]/50 block mb-1">First Name</label>
                      <Input
                        value={contact.first_name || ''}
                        onChange={(e) => updateContact(idx, 'first_name', e.target.value)}
                        placeholder="John"
                        className="bg-[#2D063E] border-[#4A0A66] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#FDF2F8]/50 block mb-1">Last Name</label>
                      <Input
                        value={contact.last_name || ''}
                        onChange={(e) => updateContact(idx, 'last_name', e.target.value)}
                        placeholder="Smith"
                        className="bg-[#2D063E] border-[#4A0A66] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#FDF2F8]/50 block mb-1">Email</label>
                      <Input
                        value={contact.email}
                        onChange={(e) => updateContact(idx, 'email', e.target.value)}
                        placeholder="john@example.com"
                        className="bg-[#2D063E] border-[#4A0A66] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#FDF2F8]/50 block mb-1">Phone</label>
                      <Input
                        value={contact.phone || ''}
                        onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className="bg-[#2D063E] border-[#4A0A66] text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-[#FDF2F8]/50 block mb-1">Role</label>
                      <Input
                        value={contact.role || ''}
                        onChange={(e) => updateContact(idx, 'role', e.target.value)}
                        placeholder="e.g., Program Director"
                        className="bg-[#2D063E] border-[#4A0A66] text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white font-medium block mb-2">Start Date</label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-[#1a0527] border-[#4A0A66] text-white h-11"
                  />
                </div>
                <div>
                  <label className="text-sm text-white font-medium block mb-2">End Date</label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="bg-[#1a0527] border-[#4A0A66] text-white h-11"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-white font-medium block mb-2">When Partnership Expires</label>
                <select
                  value={formData.expiration_behavior}
                  onChange={(e) => setFormData({ ...formData, expiration_behavior: e.target.value })}
                  className="w-full bg-[#1a0527] border border-[#4A0A66] text-white rounded-md h-11 px-3"
                >
                  <option value="downgrade_to_free">Downgrade users to Free tier</option>
                  <option value="keep_tier">Keep current tier</option>
                  <option value="suspend_account">Suspend accounts</option>
                </select>
                <p className="text-xs text-[#FDF2F8]/40 mt-2">What happens to member accounts when the partnership ends</p>
              </div>

              <div className="border-t border-[#4A0A66] pt-5 space-y-4">
                <h4 className="text-white font-medium">Display Options</h4>
                
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-[#1a0527] hover:bg-[#1a0527]/80 transition-colors">
                  <div 
                    className={`w-11 h-6 rounded-full transition-colors relative ${
                      formData.show_seats_remaining ? 'bg-[#0FFFE2]' : 'bg-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({ ...formData, show_seats_remaining: !formData.show_seats_remaining })}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      formData.show_seats_remaining ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-white">Show seats remaining</span>
                    <p className="text-xs text-[#FDF2F8]/40">Display available spots on the redeem page</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-[#1a0527] hover:bg-[#1a0527]/80 transition-colors">
                  <div 
                    className={`w-11 h-6 rounded-full transition-colors relative ${
                      formData.skip_onboarding ? 'bg-[#0FFFE2]' : 'bg-[#4A0A66]'
                    }`}
                    onClick={() => setFormData({ ...formData, skip_onboarding: !formData.skip_onboarding })}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      formData.skip_onboarding ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-white">Skip onboarding</span>
                    <p className="text-xs text-[#FDF2F8]/40">Send users directly to their journal after signup</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-5">
              <div className="bg-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
                <h4 className="text-white font-medium mb-1">Custom Branding</h4>
                <p className="text-sm text-[#FDF2F8]/50 mb-4">Customize how the redeem page looks for this partnership</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-white block mb-2">Partner Logo</label>
                    <div className="flex gap-3">
                      <Input
                        data-testid="partnership-logo-url-input"
                        value={formData.client_logo_url}
                        onChange={(e) => setFormData({ ...formData, client_logo_url: e.target.value })}
                        placeholder="Upload or paste URL"
                        className="bg-[#2D063E] border-[#4A0A66] text-white flex-1"
                      />
                      <label className="cursor-pointer bg-[#4A0A66] hover:bg-[#5A1A76] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors">
                        <Upload className="w-4 h-4" />
                        Upload
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error('Logo must be less than 2MB');
                              return;
                            }
                            try {
                              const formDataUpload = new FormData();
                              formDataUpload.append('file', file);
                              const res = await axios.post(`${API_BASE}/api/admin/upload/image`, formDataUpload, {
                                withCredentials: true,
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              const fullUrl = `${API_BASE}${res.data.url}`;
                              setFormData({ ...formData, client_logo_url: fullUrl });
                              toast.success('Logo uploaded');
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to upload logo');
                            }
                          }}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-[#FDF2F8]/40 mt-1">Max 2MB • JPEG, PNG, GIF, WebP</p>
                  </div>

                  <div>
                    <label className="text-sm text-white block mb-2">Brand Color</label>
                    <div className="flex gap-3">
                      <Input
                        data-testid="partnership-brand-color-input"
                        value={formData.brand_color}
                        onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                        placeholder="#DF1283"
                        className="bg-[#2D063E] border-[#4A0A66] text-white flex-1 font-mono"
                      />
                      <input
                        type="color"
                        value={formData.brand_color || '#DF1283'}
                        onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                        className="w-11 h-11 rounded-lg cursor-pointer border border-[#4A0A66]"
                      />
                    </div>
                    <p className="text-xs text-[#FDF2F8]/40 mt-1">Used for buttons and accents on the redeem page</p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {(formData.client_logo_url || formData.brand_color) && (
                <div className="bg-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
                  <h4 className="text-sm text-[#FDF2F8]/60 mb-3">Preview</h4>
                  <div className="flex items-center gap-4">
                    {formData.client_logo_url && (
                      <img 
                        src={formData.client_logo_url} 
                        alt="Partner logo" 
                        className="h-12 object-contain rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    {formData.brand_color && (
                      <div 
                        className="px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                        style={{ background: formData.brand_color }}
                      >
                        Sample Button
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#4A0A66] flex justify-end gap-3 shrink-0">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
          >
            Cancel
          </Button>
          <Button
            data-testid="partnership-save-button"
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white px-6"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Create Partnership'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Partnerships Manager Component
const PartnershipsManager = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailPartnership, setDetailPartnership] = useState(null);
  const [partnershipEmailTemplates, setPartnershipEmailTemplates] = useState([]);
  const [emailDropdownOpen, setEmailDropdownOpen] = useState(null); // partnership_id of open dropdown
  const [sendingEmail, setSendingEmail] = useState(null); // partnership_id currently sending

  const fetchData = async () => {
    setLoading(true);
    try {
      const [partnershipsRes, typesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/partnerships`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/admin/partnership-types`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/admin/partnerships/analytics/summary`, { withCredentials: true })
      ]);
      
      setPartnerships(partnershipsRes.data.partnerships || []);
      setTypes(typesRes.data.types || []);
      setSummary(summaryRes.data.summary || null);
    } catch (err) {
      console.error('Error fetching partnerships:', err);
      toast.error('Failed to load partnerships');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPartnershipEmailTemplates();
  }, []);

  // Fetch partnership email templates
  const fetchPartnershipEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email-templates`, { withCredentials: true });
      // API returns {templates: [...]} format
      const allTemplates = res.data?.templates || res.data || [];
      // Filter to only partnership category templates
      const partnershipTemplates = allTemplates.filter(t => t.category === 'partnership');
      setPartnershipEmailTemplates(partnershipTemplates);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    }
  };

  // Send partner email with selected template
  const sendPartnerEmail = async (partnership, templateType) => {
    try {
      setSendingEmail(partnership.partnership_id);
      setEmailDropdownOpen(null);
      
      await axios.post(
        `${API_BASE}/api/admin/partnerships/${partnership.partnership_id}/send-partner-email`,
        { template_type: templateType },
        { withCredentials: true }
      );
      
      const contactEmail = partnership.contacts?.[0]?.email || 'contact';
      toast.success(`Email sent to ${contactEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleDelete = async (partnershipId) => {
    if (!window.confirm('Delete this partnership? User accounts will NOT be deleted.')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/admin/partnerships/${partnershipId}`, { withCredentials: true });
      toast.success('Partnership deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete partnership');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const copyRedeemUrl = (code) => {
    const url = `${window.location.origin}/redeem?code=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Redeem URL copied!');
  };

  const filteredPartnerships = partnerships.filter(p =>
    p.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.access_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#0FFFE2';
      case 'paused': return '#FFD700';
      case 'expired': return '#ff6b6b';
      case 'cancelled': return '#888';
      default: return '#4A0A66';
    }
  };

  const getTypeColor = (typeId) => {
    const type = types.find(t => t.id === typeId);
    return type?.color || '#4A0A66';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
            <div className="text-2xl font-bold text-[#0FFFE2]">{summary.total_partnerships}</div>
            <div className="text-xs text-[#FDF2F8]/60">Total Partnerships</div>
          </div>
          <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{summary.total_seats_used} / {summary.total_seats_allocated}</div>
            <div className="text-xs text-[#FDF2F8]/60">Seats Used ({summary.overall_utilization_rate}%)</div>
          </div>
          <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
            <div className="text-2xl font-bold text-[#FFD700]">{summary.engagement_rate}%</div>
            <div className="text-xs text-[#FDF2F8]/60">Engagement Rate ({summary.users_with_entries} active)</div>
          </div>
          <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
            <div className="text-2xl font-bold text-[#FF7901]">${(summary.total_revenue + summary.total_donated_value).toLocaleString()}</div>
            <div className="text-xs text-[#FDF2F8]/60">Total Value (${summary.total_revenue} paid)</div>
          </div>
        </div>
      )}

      {/* Analytics Dashboard */}
      <PartnershipAnalytics summary={summary} />

      {/* Partnership Types Manager */}
      <PartnershipTypesManager types={types} onTypesUpdate={fetchData} />

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search partnerships..."
            className="pl-10 bg-[#1a0527] border-[#4A0A66] text-white"
          />
        </div>
        
        <Button
          data-testid="partnership-new-button"
          onClick={() => { setEditingPartnership(null); setShowModal(true); }}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Partnership
        </Button>
      </div>

      {/* Partnerships List */}
      <div className="space-y-3">
        {filteredPartnerships.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No partnerships yet. Create your first partnership to get started.</p>
          </div>
        ) : (
          filteredPartnerships.map(p => (
            <div 
              data-testid={`partnership-card-${p.partnership_id}`}
              key={p.partnership_id}
              className="bg-[#1a0527] border border-[#4A0A66] rounded-lg overflow-hidden"
            >
              {/* Main Row */}
              <div 
                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[#2D063E]/50"
                onClick={() => setExpandedId(expandedId === p.partnership_id ? null : p.partnership_id)}
              >
                <div 
                  className="w-2 h-12 rounded-full"
                  style={{ background: getStatusColor(p.status) }}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-semibold truncate">{p.organization_name}</h4>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: `${getTypeColor(p.partnership_type)}20`, color: getTypeColor(p.partnership_type) }}
                    >
                      {types.find(t => t.id === p.partnership_type)?.label || p.partnership_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#FDF2F8]/60">
                    <span className="flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      {p.access_code}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {p.analytics?.accounts_created || 0} / {p.total_seats} seats
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-right mr-4">
                    <div className="text-lg font-bold text-[#0FFFE2]">{p.analytics?.utilization_rate || 0}%</div>
                    <div className="text-xs text-[#FDF2F8]/60">utilized</div>
                  </div>
                  {expandedId === p.partnership_id ? (
                    <ChevronUp className="w-5 h-5 text-white/50" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/50" />
                  )}
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedId === p.partnership_id && (
                <div className="border-t border-[#4A0A66] p-4 bg-[#2D063E]/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-[#FDF2F8]/60 mb-1">Accounts Created</div>
                      <div className="text-lg font-semibold text-white">{p.analytics?.accounts_created || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#FDF2F8]/60 mb-1">Users with Entries</div>
                      <div className="text-lg font-semibold text-[#0FFFE2]">{p.analytics?.users_with_entries || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#FDF2F8]/60 mb-1">Total Entries</div>
                      <div className="text-lg font-semibold text-[#FFD700]">{p.analytics?.total_entries || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-[#FDF2F8]/60 mb-1">Value</div>
                      <div className="text-lg font-semibold text-[#FF7901]">
                        ${p.payment_amount?.toLocaleString() || 0}
                        <span className="text-xs text-[#FDF2F8]/60 ml-1">({p.payment_status})</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contacts */}
                  {p.contacts && p.contacts.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs text-[#FDF2F8]/60 mb-2">Contacts</div>
                      <div className="flex flex-wrap gap-2">
                        {p.contacts.map((c, i) => {
                          const displayName = c.first_name || c.last_name 
                            ? `${c.first_name || ''} ${c.last_name || ''}`.trim()
                            : c.name || 'Unknown';
                          return (
                            <div key={i} className="bg-[#1a0527] px-3 py-1.5 rounded-lg text-sm">
                              <span className="text-white">{displayName}</span>
                              {c.email && <span className="text-[#FDF2F8]/60 ml-2">{c.email}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Notes */}
                  {p.notes && (
                    <div className="mb-4">
                      <div className="text-xs text-[#FDF2F8]/60 mb-1">Notes</div>
                      <p className="text-white/80 text-sm">{p.notes}</p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-[#4A0A66]">
                    {/* Email Partner Dropdown */}
                    <div className="relative">
                      <Button
                        size="sm"
                        data-testid={`partnership-email-partner-btn-${p.partnership_id}`}
                        className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setEmailDropdownOpen(emailDropdownOpen === p.partnership_id ? null : p.partnership_id);
                        }}
                        disabled={sendingEmail === p.partnership_id}
                      >
                        {sendingEmail === p.partnership_id ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Mail className="w-3 h-3 mr-1" />
                        )}
                        Email Partner
                        <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${emailDropdownOpen === p.partnership_id ? 'rotate-180' : ''}`} />
                      </Button>
                      
                      {/* Dropdown Menu - opens upward */}
                      {emailDropdownOpen === p.partnership_id && (
                        <div 
                          className="absolute bottom-full left-0 mb-1 w-64 bg-[#1a0527] border border-[#4A0A66] rounded-lg shadow-xl z-50 overflow-hidden"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2 border-b border-[#4A0A66]/50">
                            <p className="text-xs text-white/50 uppercase tracking-wider">Select Email Template</p>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {partnershipEmailTemplates.length > 0 ? (
                              partnershipEmailTemplates.map((template) => (
                                <button
                                  key={template.template_type}
                                  data-testid={`email-template-${template.template_type}`}
                                  className="w-full px-3 py-2 text-left hover:bg-[#4A0A66]/30 transition-colors flex items-start gap-2"
                                  onClick={() => sendPartnerEmail(p, template.template_type)}
                                >
                                  <Mail className="w-4 h-4 text-[#df1283] mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-white text-sm font-medium">{template.name}</p>
                                    <p className="text-white/50 text-xs line-clamp-2">{template.description || 'No description'}</p>
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-white/50 text-sm">
                                No partnership email templates found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      data-testid={`partnership-members-btn-${p.partnership_id}`}
                      className="bg-[#FF7901]/20 border border-[#FF7901]/50 text-[#FF7901] hover:bg-[#FF7901]/30"
                      onClick={(e) => { e.stopPropagation(); setDetailPartnership({...p, viewMode: 'members'}); }}
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Members ({p.analytics?.accounts_created || 0})
                    </Button>
                    <Button
                      size="sm"
                      data-testid={`partnership-analytics-btn-${p.partnership_id}`}
                      className="bg-gradient-to-r from-[#0FFFE2]/20 to-[#0FFFE2]/10 border border-[#0FFFE2]/50 text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
                      onClick={(e) => { e.stopPropagation(); setDetailPartnership({...p, viewMode: 'analytics'}); }}
                    >
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Analytics
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#4A0A66] text-white"
                      onClick={() => copyCode(p.access_code)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Code
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#4A0A66] text-white"
                      onClick={() => copyRedeemUrl(p.access_code)}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Copy Redeem URL
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#4A0A66] text-white"
                      onClick={() => { setEditingPartnership(p); setShowModal(true); }}
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      onClick={() => handleDelete(p.partnership_id)}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <PartnershipModal
          partnership={editingPartnership}
          types={types}
          onClose={() => { setShowModal(false); setEditingPartnership(null); }}
          onSave={() => { setShowModal(false); setEditingPartnership(null); fetchData(); }}
        />
      )}

      {/* Detail Analytics Modal */}
      {detailPartnership && (
        <PartnershipDetailModal
          partnership={detailPartnership}
          onClose={() => setDetailPartnership(null)}
        />
      )}
    </div>
  );
};

export default PartnershipsManager;
